import React, { useEffect, useState } from "react";
import "./Ledger.scss";
import { Link } from "react-router-dom";
import LedgerMainFilter from "./LedgerMainFilter";
import LedgerFilter from "./LedgerFilter";
import { WalletAPI } from "../../../utils/api/wallet";
import { SubscriptionApi } from "../../../utils/api/SubscriptionApi";
import CloseIcon from "../../../components/icons/CloseIcon";
import TableHeaderRow from "../../../components/tables/TableHeaderRow";
import TableHeaderCell from "../../../components/tables/TableHeaderCell";
import TableDataRow from "../../../components/tables/TableDataRow";
import TableDataCell from "../../../components/tables/TableDataCell";
import Loading from "../../loading/Loading";
import DownArrowIcon from "../../../components/icons/DownArrowIcon";
import FileDownloadIcon from "../../../components/icons/FileDownloadIcon";
import ProtectedComponent from "../../../components/ProtectedComponent";
import NavigationLayout from "../navigation";

interface LedgerStats {
  available_points: number;
  earned_referral: number;
  earned_royalty: number;
  applied_to_invoice: number;
}

interface TransactionDetails {
  fullTitle: string;
  subTitleLink: string | null;
  subTitleText: string;
  transactionId: string;
  plan: string;
  pointsUsed: string;
  amount: string;
  fullDate: string;
  status: string;
}

interface TransactionItem {
  id: string;
  transaction: string;
  subInfo: {
    text: string;
    link: string | null;
  };
  amount: string;
  type: "credit" | "debit";
  date: string;
  time: string;
  action: "invoice" | null;
  expandable: boolean;
  details?: TransactionDetails;
  invoicePdfLink?: string | null;
  isAddress?: boolean;
}

const Ledger = () => {
  const [filterOpen, setFilterOpen] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<LedgerStats>({
    available_points: 0,
    earned_referral: 0,
    earned_royalty: 0,
    applied_to_invoice: 0,
  });
  const [transactions, setTransactions] = useState<TransactionItem[]>([]);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const initialFilterValues = {
    start_date: "",
    end_date: "",
  };

  const [activeFilters, setActiveFilters] = useState(initialFilterValues);

  const handleFilterApply = (values: any) => {
    setActiveFilters(values);
    setPage(1);
    setFilterOpen(false);
  };

  const handleClearAll = () => {
    setActiveFilters(initialFilterValues);
    setPage(1);
  };

  useEffect(() => {
    fetchLedgerData();
  }, [page, activeFilters]);

  const fetchLedgerData = async () => {
    setIsLoading(true);
    try {
      const statsRes = await WalletAPI.getLedgerStats();
      if (statsRes.success && statsRes.data?.data) {
        setStats(statsRes.data.data);
      }

      const historyRes = await WalletAPI.getTransactionHistory({
        page: page,
        limit: 20,
        start_date: activeFilters.start_date,
        end_date: activeFilters.end_date,
      });

      if (historyRes.success && historyRes.data?.data) {
        const mappedTransactions = historyRes.data.data.docs.map(
          (item: any) => {
            const dateSource = item.createdAt || item.date;
            const dateObj = new Date(dateSource);

            const formattedDate = dateObj.toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            });

            const formattedTime = dateObj.toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            });

            const fullDateTimeString = dateObj.toLocaleString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            });
            return {
              ...item,
              date: formattedDate,
              time: formattedTime,
              action:
                item.action ||
                (item.transaction_type === "invoice" ? "invoice" : null),

              invoicePdfLink: item.invoicePdfLink || null,
              isAddress: item.isAddress || false,
              details: item.details
                ? {
                    ...item.details,
                    fullDate: fullDateTimeString,
                  }
                : undefined,
            };
          },
        );

        setTransactions(mappedTransactions);
        setTotalPages(historyRes.data.data.totalPages);
      }
    } catch (error) {
      console.error("Error fetching ledger data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRow = (id: string) => {
    if (expandedRowId === id) {
      setExpandedRowId(null);
    } else {
      setExpandedRowId(id);
    }
  };

  const handleInvoiceDownload = async (invoicePath: string) => {
    if (!invoicePath) return;

    const fileName = invoicePath.split("/").pop();
    if (!fileName) return;

    try {
      const res = await SubscriptionApi.downloadCustomInvoice(fileName);

      if (!res.success) {
        alert("Failed to download invoice");
        return;
      }

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      alert("An error occurred while downloading.");
    }
  };

  const formatDate = (date: string) => {
    if (!date) return "";
    const d = new Date(date);

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const renderAppliedFilters = () => {
    const chips: React.ReactNode[] = [];

    // if (activeFilters.transaction_type) {
    //   chips.push(
    //     <div className="filter-chip font-inter-400" key="type">
    //       <span>Type: {activeFilters.transaction_type}</span>

    //       <span
    //         className="close-chip"
    //         onClick={() =>
    //           setActiveFilters((prev) => ({ ...prev, transaction_type: "" }))
    //         }
    //       >
    //         ×
    //       </span>
    //     </div>,
    //   );
    // }

    if (activeFilters.start_date && activeFilters.end_date) {
      chips.push(
        <div className="filter-chip font-inter-400" key="date">
          <span>
            Transaction date: {formatDate(activeFilters.start_date)} -{" "}
            {formatDate(activeFilters.end_date)}
          </span>

          <span
            className="close-chip"
            onClick={() =>
              setActiveFilters((prev) => ({
                ...prev,
                start_date: "",
                end_date: "",
              }))
            }
          >
            <CloseIcon />
          </span>
        </div>,
      );
    }

    if (chips.length === 0) return null;

    return (
      <div className="applied-filters-container">
        <p className="applied-label font-inter-400">Applied filters</p>

        <div className="chips-row">
          {chips}

          <button
            className="clear-all-btn font-inter-400"
            onClick={handleClearAll}
          >
            Clear all filters
          </button>
        </div>
      </div>
    );
  };

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="ledger-container">
          <h2 className="ledger-page-title font-inter-500">Ledger</h2>

          <p className="ledger-sub-title font-inter-400">
            Track your referral and royalty points, monitor usage, and view your
            full transaction history
          </p>

          <div className="stats-grid">
            <div className="stat-card large-card">
              <p className="card-label font-inter-400">Available points</p>
              <h3 className="large-card-card-value font-inter-500">
                {stats.available_points}
              </h3>
            </div>
          </div>

          <div className="stats-grid three-col">
            <div className="stat-card">
              <p className="three-col-card-label font-inter-400">
                Points Earned (Referral)
              </p>
              <h3 className="three-col-card-value font-inter-400">
                {stats.earned_referral}
              </h3>
            </div>
            <div className="stat-card">
              <p className="three-col-card-label font-inter-400">
                Points Earned (Royalty)
              </p>
              <h3 className="three-col-card-value font-inter-400">
                {stats.earned_royalty}
              </h3>
            </div>
            <div className="stat-card">
              <p className="three-col-card-label font-inter-400">
                Points Applied to Invoice
              </p>
              <h3 className="three-col-card-value font-inter-400">
                {stats.applied_to_invoice}
              </h3>
            </div>
          </div>

          <div className="history-header">
            <h3 className="font-inter-500">Transaction history</h3>
            <LedgerMainFilter />
          </div>

          {renderAppliedFilters()}

          <div className="ledger-table-container">
            <table>
              <TableHeaderRow className="table-header-row">
                <TableHeaderCell minWidth={200}>TRANSACTION</TableHeaderCell>
                <TableHeaderCell minWidth={120}>AMOUNT</TableHeaderCell>
                <TableHeaderCell minWidth={180}>DATE</TableHeaderCell>
                <TableHeaderCell minWidth={120}>ACTION</TableHeaderCell>
                <TableHeaderCell minWidth={250}>TRANSACTION ID</TableHeaderCell>
              </TableHeaderRow>

              <tbody>
                {isLoading ? (
                  <TableDataRow className="contain-row">
                    <TableDataCell minWidth={200}>
                      <Loading />
                    </TableDataCell>
                  </TableDataRow>
                ) : transactions.length === 0 ? (
                  <TableDataRow className="contain-row">
                    <TableDataCell minWidth={200}>
                      <p className="no-found font-inter-400">
                        No transactions found.
                      </p>
                    </TableDataCell>
                  </TableDataRow>
                ) : (
                  transactions.map((item) => (
                    <React.Fragment key={item.id}>
                      <TableDataRow
                        className={`contain-row ${expandedRowId === item.id ? "expanded" : ""}`}
                        onClick={() => item.expandable && toggleRow(item.id)}
                      >
                        <TableDataCell minWidth={200}>
                          <div className="transaction-cell">
                            <div className="text-wrapper">
                              <span className="main-text font-inter-400">
                                {item.transaction}
                              </span>
                              {item.subInfo &&
                                (item.subInfo.link ? (
                                  <Link
                                    to={item.subInfo.link}
                                    className="sub-text link font-inter-400"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {item.subInfo.text}
                                  </Link>
                                ) : (
                                  <span
                                    className={`sub-text font-inter-400 ${item.isAddress ? "blue-text" : ""}`}
                                  >
                                    {item.subInfo.text}
                                  </span>
                                ))}
                            </div>
                            {item.expandable && (
                              <div
                                className={`arrow-wrapper ${expandedRowId === item.id ? "rotate" : ""}`}
                              >
                                <DownArrowIcon />
                              </div>
                            )}
                          </div>
                        </TableDataCell>

                        <TableDataCell minWidth={120}>
                          <span
                            className={`amount-text font-inter-400 ${item.type === "credit" ? "credit" : "debit"}`}
                          >
                            {item.amount}
                          </span>
                        </TableDataCell>

                        <TableDataCell minWidth={180}>
                          <div className="date-cell">
                            <p className="date-text font-inter-400">
                              {item.date}
                            </p>
                            <p className="time-text font-inter-400">
                              {item.time}
                            </p>
                          </div>
                        </TableDataCell>

                        <TableDataCell minWidth={120}>
                          {item.action === "invoice" && item.invoicePdfLink ? (
                            <button
                              className="invoice-btn font-inter-500"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleInvoiceDownload(item.invoicePdfLink!);
                              }}
                            >
                              <FileDownloadIcon /> Invoice
                            </button>
                          ) : item.action === "invoice" ? (
                            <span className="no-action font-inter-400">
                              N/A
                            </span>
                          ) : (
                            <span className="no-action font-inter-400">-</span>
                          )}
                        </TableDataCell>

                        <TableDataCell minWidth={250}>
                          <span className="transaction-id font-inter-400">
                            {item.id}
                          </span>
                        </TableDataCell>
                      </TableDataRow>

                      {expandedRowId === item.id && item.details && (
                        <tr className="expanded-details-row">
                          <td colSpan={5}>
                            <div className="details-container">
                              <div className="details-header">
                                <h4 className="font-inter-400">
                                  {item.details.fullTitle}
                                </h4>
                                {item.details.subTitleLink ? (
                                  <Link
                                    to={item.details.subTitleLink}
                                    className="sub-link font-inter-400"
                                  >
                                    {item.details.subTitleText}
                                  </Link>
                                ) : (
                                  <p className="sub-text font-inter-400">
                                    {item.details.subTitleText}
                                  </p>
                                )}
                              </div>

                              <div className="details-grid">
                                <div className="grid-col">
                                  <div className="info-block">
                                    <label className="font-inter-400">
                                      Transaction id
                                    </label>
                                    <p className="font-inter-400">
                                      {item.details.transactionId}
                                    </p>
                                  </div>
                                  <div className="info-block">
                                    <label className="font-inter-400">
                                      Amount
                                    </label>
                                    <p className="font-inter-400">
                                      {item.details.amount}
                                    </p>
                                  </div>
                                </div>

                                <div className="grid-col">
                                  <div className="info-block">
                                    <label className="font-inter-400">
                                      Plan
                                    </label>
                                    <p className="plan-name font-inter-400">
                                      {item.details.plan}
                                    </p>
                                  </div>
                                  <div className="info-block">
                                    <label className="font-inter-400">
                                      Date
                                    </label>
                                    <p className="font-inter-400">
                                      {item.details.fullDate}
                                    </p>
                                  </div>
                                </div>

                                <div className="grid-col">
                                  <div className="info-block">
                                    <label className="font-inter-400">
                                      Points used
                                    </label>
                                    <p className="font-inter-400">
                                      {item.details.pointsUsed}
                                    </p>
                                  </div>
                                  <div className="info-block">
                                    <label className="font-inter-400">
                                      Status
                                    </label>
                                    <span className="status-badge paid font-inter-500">
                                      {item.details.status}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>

            <LedgerFilter
              currentFilters={activeFilters}
              onApply={handleFilterApply}
              onClear={handleClearAll}
            />
          </div>
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default Ledger;
